import { ChevronDown } from 'lucide-react';
import { useState, useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import { toast } from 'react-toastify';
import 'react-phone-input-2/lib/style.css';

import {
  useCreateAdminMutation,
  useChechkEmailMutation
} from '../../services/adminAuditsApi/adminAuditApi';
import Button from '../Common/Button';

const TenantOnboardingForm = () => {
  const [formData, setFormData] = useState({
    tenantName: '',
    dbName: '',
    emailId: '',
    phoneNumber: '91',
    address: '',
    institutionName: '',
    noOfSeats: 0,
    dashboards: [9001],
    planInDays: 0,
    proxyTenantCoordinatorEmail: '',
    proxyTenantCoordinatorName: '',
    proxyTenantCoordinatorPhoneNumber: '+91',
    proxyCoordinatorActive: false
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [proxyEmailError, setProxyEmailError] = useState('');

  const [createAdmin, { isLoading: isCreating }] = useCreateAdminMutation();
  const [checkEmail] = useChechkEmailMutation();
  const [isProxyEmailError, setIsProxyEmailError] = useState('');

  const availableDashboards = [
    { id: 9001, name: 'Company Tests' },
    { id: 9002, name: 'Tech Interview' },
    { id: 9003, name: 'HR Interview' },
    { id: 9004, name: 'Code Sense AI' },
    { id: 9006, name: 'Schedule Test' },
    { id: 9007, name: 'Custom Written Test' },
    { id: 9008, name: 'Drive Test' },
    { id: 9009, name: 'Cloud Prep' }
  ];

  const isValidEmail = (email = '') =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isValidIndianPhone = (phone = '') => {
    const normalized = phone.replace(/\s+/g, '');
    if (/^\+91[6-9]\d{9}$/.test(normalized)) return true;
    if (/^0[6-9]\d{9}$/.test(normalized)) return true;
    if (/^[6-9]\d{9}$/.test(normalized)) return true;
    return false;
  };

  const validateEmailMatch = (data: any) => {
    // Only validate email match if both emails have values
    if (
      data.emailId?.trim() &&
      data.proxyTenantCoordinatorEmail?.trim() &&
      data.emailId.trim() === data.proxyTenantCoordinatorEmail.trim()
    ) {
      setProxyEmailError('Proxy Email cannot be the same as Admin Email.');
    } else {
      setProxyEmailError('');
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;

    const updated = {
      ...formData,
      [name]: value
    };

    if (name === 'emailId') {
      updated.emailId = value.toLowerCase();
      if (value.trim() && !isValidEmail(value)) {
        setEmailError('Please enter a valid Email.');
      } else {
        setEmailError('');
      }
    }

    if (name === 'proxyTenantCoordinatorEmail') {
      updated.proxyTenantCoordinatorEmail = value.toLowerCase();
      if (value.trim() && !isValidEmail(value)) {
        setIsProxyEmailError('Please enter a valid Email.');
      } else {
        setIsProxyEmailError('');
      }
    }

    validateEmailMatch(updated);

    setFormData(updated);
  };

  const handlePhoneChange = (value: string, country: any) => {
    const formatted = value.startsWith('+') ? value : `+${value}`;
    setFormData(prev => ({ ...prev, phoneNumber: formatted }));
  };

  const handleProxyPhoneChange = (value: string) => {
    const formatted = value.startsWith('+') ? value : `+${value}`;

    const updated = {
      ...formData,
      proxyTenantCoordinatorPhoneNumber: formatted
    };

    validateEmailMatch(updated);

    setFormData(updated);
  };

  const toggleDashboard = (dashboardId: number) => {
    setFormData(prev => ({
      ...prev,
      dashboards: prev.dashboards.includes(dashboardId)
        ? prev.dashboards.filter(id => id !== dashboardId)
        : [...prev.dashboards, dashboardId]
    }));
  };

  const getSelectedDashboardNames = () => {
    return (
      availableDashboards
        .filter(d => formData.dashboards.includes(d.id))
        .map(d => d.name)
        .join(', ') || 'Select dashboards'
    );
  };

  const isFormValid = useMemo(() => {
    const anyProxyEntered =
      formData.proxyTenantCoordinatorEmail.trim() !== '' ||
      formData.proxyTenantCoordinatorName.trim() !== '' ||
      formData.proxyTenantCoordinatorPhoneNumber.trim() !== '+91';

    const allProxyValid =
      !anyProxyEntered ||
      (isValidEmail(formData.proxyTenantCoordinatorEmail) &&
        formData.proxyTenantCoordinatorName.trim() !== '' &&
        isValidIndianPhone(formData.proxyTenantCoordinatorPhoneNumber));

    return (
      formData.tenantName.trim() !== '' &&
      formData.dbName.trim() !== '' &&
      isValidEmail(formData.emailId) &&
      isValidIndianPhone(formData.phoneNumber) &&
      formData.address.trim() !== '' &&
      formData.institutionName.trim() !== '' &&
      Number(formData.noOfSeats) > 0 &&
      Array.isArray(formData.dashboards) &&
      formData.dashboards.length > 0 &&
      Number(formData.planInDays) > 0 &&
      allProxyValid
    );
  }, [formData]);

  const checkDuplicateEmail = async () => {
    if (formData.proxyTenantCoordinatorEmail.trim()) {
      const proxyValid = await checkDuplicateProxyEmail();

      if (!proxyValid) {
        return;
      }
    }
    try {
      const response = await checkEmail({ emailId: formData.emailId }).unwrap();

      if (response?.status && response?.isValid === true) {
        onBoardTenant();
      } else {
        setEmailError(
          'Tenant Admin Email already exists. Please use a different email.'
        );
      }
    } catch (error) {
      console.error('Error checking tenant email:', error);
    }
  };

  const checkDuplicateProxyEmail = async () => {
    try {
      const response = await checkEmail({
        emailId: formData.proxyTenantCoordinatorEmail
      }).unwrap();

      if (response?.status && response?.isValid === true) {
        return true;
      } else {
        setProxyEmailError(
          'Proxy Email already exists. Please use a different email.'
        );
        return false;
      }
    } catch (error) {
      console.error('Error checking proxy email:', error);
      return false;
    }
  };

  const onBoardTenant = async () => {
    try {
      const isProxy = !!formData?.proxyTenantCoordinatorEmail;
      const payload = { ...formData, proxyCoordinatorActive: isProxy };
      await createAdmin({ body: payload }).unwrap();
      setFormData({
        tenantName: '',
        dbName: '',
        emailId: '',
        phoneNumber: '91',
        address: '',
        institutionName: '',
        noOfSeats: 0,
        dashboards: [9001],
        planInDays: 0,
        proxyTenantCoordinatorEmail: '',
        proxyTenantCoordinatorName: '',
        proxyTenantCoordinatorPhoneNumber: '+91',
        proxyCoordinatorActive: false
      });
      toast.success('Admin created successfully');
    } catch (error) {
      console.error('Error during onboarding:', error);
      toast.error('Failed to create admin');
    }
  };

  return (
    <div className='h-full bg-gradient-to-br from-blue-50 to-indigo-100 p-8'>
      <div className='mx-auto max-w-4xl rounded-2xl bg-white p-8 shadow-xl'>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Tenant Name
              <span className='ml-1 text-[red]'>*</span>
            </label>
            <input
              type='text'
              name='tenantName'
              value={formData.tenantName}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Database Name <span className='ml-1 text-[red]'>*</span>
            </label>
            <input
              type='text'
              name='dbName'
              value={formData.dbName}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Email ID <span className='ml-1 text-[red]'>*</span>
            </label>
            <input
              type='email'
              name='emailId'
              value={formData.emailId.toLowerCase()}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
            />
            {formData.emailId !== '' && emailError !== '' && (
              <p className='mt-1 text-xs text-red-500'>{emailError}</p>
            )}
          </div>

          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Phone Number <span className='ml-1 text-[red]'>*</span>
            </label>
            <PhoneInput
              country={'in'}
              countryCodeEditable={false}
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              inputClass='!w-full !rounded-lg !border !border-gray-300 !px-4 !py-2 !bg-white focus:!border-transparent focus:!ring-2 focus:!ring-blue-500'
              containerClass='w-full'
              specialLabel=''
            />
            {formData.phoneNumber !== '91' &&
              !isValidIndianPhone(formData.phoneNumber) && (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid Indian mobile number.
                </p>
              )}
          </div>

          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Address <span className='ml-1 text-[red]'>*</span>
            </label>
            <input
              type='text'
              name='address'
              value={formData.address}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Institution Name <span className='ml-1 text-[red]'>*</span>
            </label>
            <input
              type='text'
              name='institutionName'
              value={formData.institutionName}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Number of Seats <span className='ml-1 text-[red]'>*</span>
            </label>
            <input
              type='number'
              name='noOfSeats'
              value={formData.noOfSeats}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
            />
          </div>

          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Plan Duration (In Days) <span className='ml-1 text-[red]'>*</span>
            </label>
            <input
              type='number'
              name='planInDays'
              value={formData?.planInDays}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none'
            />
          </div>

          <div className='md:col-span-2'>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Dashboards <span className='ml-1 text-[red]'>*</span>
            </label>
            <div className='relative'>
              <button
                type='button'
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className='flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
              >
                <span className='truncate'>{getSelectedDashboardNames()}</span>
                <ChevronDown
                  className={`h-5 w-5 transition-transform ${
                    isDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <div className='absolute z-10 mt-2 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg'>
                  {availableDashboards.map(dashboard => (
                    <label
                      key={dashboard.id}
                      className='flex cursor-pointer items-center px-4 py-3 hover:bg-gray-50'
                    >
                      <input
                        type='checkbox'
                        checked={formData.dashboards.includes(dashboard.id)}
                        onChange={() => toggleDashboard(dashboard.id)}
                        className='h-4 w-4 text-blue-600 focus:ring-blue-500'
                      />
                      <span className='ml-3 text-gray-700'>
                        {dashboard.name}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Proxy Admin Name{' '}
              {(formData?.proxyTenantCoordinatorEmail ||
                formData?.proxyTenantCoordinatorName ||
                formData?.proxyTenantCoordinatorPhoneNumber !== '+91') && (
                <span className='ml-1 text-[red]'>*</span>
              )}
            </label>
            <input
              type='text'
              name='proxyTenantCoordinatorName'
              value={formData?.proxyTenantCoordinatorName || ''}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none'
            />
          </div>
          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Proxy Admin Email Id{' '}
              {(formData?.proxyTenantCoordinatorEmail ||
                formData?.proxyTenantCoordinatorName ||
                formData?.proxyTenantCoordinatorPhoneNumber !== '+91') && (
                <span className='ml-1 text-[red]'>*</span>
              )}
            </label>
            <input
              type='text'
              name='proxyTenantCoordinatorEmail'
              value={formData?.proxyTenantCoordinatorEmail || ''}
              onChange={handleInputChange}
              className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none'
            />
            {formData.proxyTenantCoordinatorEmail !== '' &&
              isProxyEmailError !== '' && (
                <p className='mt-1 text-xs text-red-500'>{isProxyEmailError}</p>
              )}
            {proxyEmailError && (
              <p className='mt-1 text-xs text-red-500'>{proxyEmailError}</p>
            )}
          </div>
          <div>
            <label className='mb-2 block text-sm font-semibold text-gray-700'>
              Proxy Phone Number{' '}
              {(formData?.proxyTenantCoordinatorEmail ||
                formData?.proxyTenantCoordinatorName ||
                formData?.proxyTenantCoordinatorPhoneNumber !== '+91') && (
                <span className='ml-1 text-[red]'>*</span>
              )}
            </label>
            <PhoneInput
              country={'in'}
              countryCodeEditable={false}
              value={formData?.proxyTenantCoordinatorPhoneNumber || ''}
              onChange={handleProxyPhoneChange}
              inputClass='!w-full !rounded-lg !border !border-gray-300 !px-4 !py-2 !bg-white focus:!border-transparent focus:!ring-2 focus:!ring-blue-500'
              containerClass='w-full'
              specialLabel=''
            />
            {formData.proxyTenantCoordinatorPhoneNumber !== '+91' &&
              !isValidIndianPhone(
                formData.proxyTenantCoordinatorPhoneNumber
              ) && (
                <p className='mt-1 text-xs text-red-500'>
                  Please enter a valid Indian mobile number.
                </p>
              )}
          </div>
          <div className='flex justify-end md:col-span-2'>
            <Button
              label='Submit'
              variant='contained'
              disabled={!isFormValid || isCreating || proxyEmailError !== ''}
              handleClickBtn={checkDuplicateEmail}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantOnboardingForm;
